import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, ListGroup, Badge, ProgressBar, Tabs, Tab } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaPlay, FaVideo } from 'react-icons/fa';

import { selectAuthUserId } from '../../redux/authSlice';
import { selectCourses } from '../../redux/coursesSlice';
import { fetchCourseByIdWithUnits } from '../../services/supabaseClient/courseService';
import { fetchVideosByCourse } from '../../services/supabaseClient/videoLibraryService';
// eslint-disable-next-line import/order
import FancyButton from './fancyButton';
import CourseLearners from './CourseLearners';
import LoadingSpinner from './LoadingSpinner';

import './CourseDetailsModal.css';

const CourseDetailsModal = ({
  show,
  onHide,
  course,
  onStart,
  onStartAnimated,
  onStartScene,
  onStartExam,
  onFork,
  onJumpToLesson,
}) => {
  const navigate = useNavigate();
  const currentUserId = useSelector(selectAuthUserId);
  const allCourses = useSelector(selectCourses);
  const [hydratedCourse, setHydratedCourse] = useState(null);

  // Try to get full course data from Redux, fallback to passed course prop
  const fullCourse = hydratedCourse || allCourses.find((c) => c.id === course.id) || course;

  const isOwner = fullCourse.owner_id === currentUserId;
  const isAuthenticated = !!currentUserId;
  const courseDifficulty = fullCourse.difficultyLevel || fullCourse.difficulty_level || 'Beginner';

  const currentLessonIndex =
    typeof fullCourse?.currentLessonIndex === 'number' ? fullCourse.currentLessonIndex : 0;
  const unitCount = fullCourse.units?.length || 1;
  const hasZeroBasedPosition =
    Array.isArray(fullCourse?.units) &&
    fullCourse.units.some(
      (unit) => Number.isFinite(Number(unit?.position)) && Number(unit.position) === 0
    );

  const resolveUnitTitle = useCallback(
    (unit, index) => {
      const isGenericLabel = (value) =>
        /^((unit|lesson)\s*\d+|topic\s*\d+)$/i.test(String(value || '').trim());

      const extractJsonTitle = (value) => {
        if (typeof value !== 'string') return '';
        const trimmed = value.trim();
        if (!trimmed.startsWith('{')) return '';
        try {
          const parsed = JSON.parse(trimmed);
          const jsonCandidates = [
            parsed?.title,
            parsed?.unitTitle,
            parsed?.unit_title,
            parsed?.lessonTitle,
            parsed?.lesson_title,
            parsed?.name,
            parsed?.topic,
          ];
          return (
            jsonCandidates
              .map((candidate) => (typeof candidate === 'string' ? candidate.trim() : ''))
              .find(Boolean) || ''
          );
        } catch {
          return '';
        }
      };

      const fromGraph = Array.isArray(fullCourse?.knowledgeGraph)
        ? fullCourse.knowledgeGraph.find((node) => node?.id === unit?.kgTopicId) ||
          fullCourse.knowledgeGraph[index]
        : null;

      const candidates = [
        unit?.unit_title,
        unit?.lesson_title,
        unit?.lessonTitle,
        unit?.name,
        unit?.topic,
        unit?.title,
        fromGraph?.title,
      ]
        .map((value) => {
          if (typeof value !== 'string') return '';
          const jsonTitle = extractJsonTitle(value);
          const normalized = (jsonTitle || value).replace(/^unit\s+\d+\s*[:.-]?\s*/i, '').trim();
          return normalized;
        })
        .filter(Boolean)
        .filter((value) => !isGenericLabel(value));

      return candidates[0] || `Lesson ${index + 1}`;
    },
    [fullCourse?.knowledgeGraph]
  );

  // Local state for temporary selection when viewing past lessons
  const [selectedLessonIndex, setSelectedLessonIndex] = useState(currentLessonIndex);
  const [launchMode, setLaunchMode] = useState('classroom');
  const [activeTab, setActiveTab] = useState('units');
  const [courseVideos, setCourseVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);

  useEffect(() => {
    setSelectedLessonIndex(currentLessonIndex);
  }, [fullCourse, currentLessonIndex]);

  useEffect(() => {
    let cancelled = false;
    const shouldHydrate =
      show &&
      Boolean(course?.id) &&
      (!Array.isArray(fullCourse?.units) || fullCourse.units.length === 0);
    if (!shouldHydrate) return () => {};

    (async () => {
      const { data } = await fetchCourseByIdWithUnits(course.id);
      if (!cancelled && data) {
        setHydratedCourse(data);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, course?.id, fullCourse?.units]);

  useEffect(() => {
    if (show) {
      setLaunchMode('classroom');
    }
  }, [show]);

  const loadCourseVideos = useCallback(async () => {
    setLoadingVideos(true);

    // Add minimum delay for smooth transition
    const [{ data, error }] = await Promise.all([
      fetchVideosByCourse(currentUserId, fullCourse.id),
      new Promise((resolve) => setTimeout(resolve, 500)), // 500ms minimum delay
    ]);

    if (!error && data) {
      setCourseVideos(data);
    }
    setLoadingVideos(false);
  }, [currentUserId, fullCourse.id]);

  // Fetch videos when Videos tab is active
  useEffect(() => {
    if (activeTab === 'videos' && fullCourse.id && currentUserId) {
      loadCourseVideos();
    }
  }, [activeTab, fullCourse.id, currentUserId, loadCourseVideos]);

  const handlePlayVideo = (video) => {
    navigate(`/video/${video.id}`);
    onHide();
  };

  const getUnitStatus = (index) => {
    if (index === selectedLessonIndex) return 'active'; // selected = active
    if (index < currentLessonIndex) return 'completed';
    if (index === currentLessonIndex) return 'idle'; // not selected but accessible
    return 'locked'; // only block units ahead of current progress
  };

  const isClickable = (index) => isOwner && index <= currentLessonIndex;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="ml-course-modal"
      dialogClassName="ml-course-modal__dialog"
      contentClassName="ml-course-modal__content"
    >
      <Modal.Header closeButton className="ml-course-modal__header">
        <Modal.Title>
          {fullCourse.title}
          <Badge
            bg={
              courseDifficulty?.toLowerCase() === 'beginner'
                ? 'success'
                : courseDifficulty?.toLowerCase() === 'intermediate'
                  ? 'warning'
                  : 'danger'
            }
            className="ms-2"
          >
            {courseDifficulty}
          </Badge>
          {fullCourse.ownerId !== fullCourse.creatorId && (
            <Badge bg="light" text="dark" className="ms-2">
              Forked (Level {fullCourse.forkLevel})
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="ml-course-modal__body">
        {isOwner && (
          <div className="course-stats mb-4">
            <div className="stat-item">
              <h5>Progress</h5>
              <ProgressBar
                now={(currentLessonIndex / unitCount) * 100}
                variant="info"
                label={`${Math.round((currentLessonIndex / unitCount) * 100)}%`}
              />
            </div>
          </div>
        )}

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
          <Tab eventKey="units" title="Units">
            <div className="stat-item">
              {!fullCourse.units || fullCourse.units.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fa-solid fa-book fa-3x mb-3 text-muted" />
                  <p className="text-muted">No units available for this course yet.</p>
                </div>
              ) : (
                <ListGroup className="mb-4">
                  {fullCourse.units.map((unit, index) => {
                    const unitStatus = isOwner ? getUnitStatus(index) : '';
                    const unitPosition =
                      Number.isFinite(Number(unit?.position)) && Number(unit.position) > 0
                        ? hasZeroBasedPosition
                          ? Number(unit.position) + 1
                          : Number(unit.position)
                        : index + 1;
                    return (
                      <ListGroup.Item
                        key={index}
                        className={`d-flex justify-content-between align-items-center unit-item ${unitStatus} ${
                          isClickable(index) ? 'clickable-unit' : ''
                        }`}
                        onClick={() => {
                          if (isClickable(index)) {
                            setSelectedLessonIndex(index);
                            if (typeof onJumpToLesson === 'function') {
                              onJumpToLesson(index);
                            }
                          }
                        }}
                      >
                        <div className="unit-item-main">
                          <strong className="unit-item-label">{unitPosition}</strong>
                          <span className="unit-item-title">{resolveUnitTitle(unit, index)}</span>
                        </div>

                        {unitStatus === 'completed' && (
                          <Badge bg="success" className="status-badge">
                            Completed
                          </Badge>
                        )}
                        {unitStatus === 'reviewing' && (
                          <Badge bg="secondary" className="status-badge">
                            Reviewing
                          </Badge>
                        )}
                        {unitStatus === 'active' && (
                          <Badge bg="info" className="status-badge">
                            In Progress
                          </Badge>
                        )}
                        {unitStatus === 'locked' && (
                          <Badge bg="secondary" className="status-badge" style={{ opacity: 0.5 }}>
                            Locked
                          </Badge>
                        )}
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
            </div>
          </Tab>

          <Tab eventKey="videos" title={`Videos`}>
            <div className="stat-item">
              {loadingVideos ? (
                <LoadingSpinner message="Loading videos..." size="sm" />
              ) : courseVideos.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted mb-0">No videos</p>
                </div>
              ) : (
                <ListGroup className="mb-4">
                  {courseVideos.map((video) => (
                    <ListGroup.Item
                      key={video.id}
                      className="d-flex justify-content-between align-items-center video-list-item"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handlePlayVideo(video)}
                    >
                      <div className="d-flex align-items-center flex-grow-1">
                        <div className="video-thumbnail-small me-3">
                          {video.thumbnail_url ? (
                            <img
                              src={video.thumbnail_url}
                              alt={video.title}
                              style={{
                                width: '120px',
                                height: '68px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: '120px',
                                height: '68px',
                                background: '#f0f0f0',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <FaVideo size={24} className="text-muted" />
                            </div>
                          )}
                        </div>
                        <div className="flex-grow-1">
                          <strong className="d-block">{video.title}</strong>
                          {video.description && (
                            <small className="text-muted d-block">
                              {video.description.length > 100
                                ? `${video.description.substring(0, 100)}...`
                                : video.description}
                            </small>
                          )}
                          {video.tags && video.tags.length > 0 && (
                            <div className="mt-1">
                              {video.tags.slice(0, 3).map((tag, index) => (
                                <Badge key={index} bg="secondary" className="me-1">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayVideo(video);
                        }}
                      >
                        <FaPlay className="me-1" />
                        Watch
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>
          </Tab>

          {isOwner && (
            <Tab eventKey="Coursemates" title="Coursemates">
              {activeTab === 'Coursemates' && (
                <CourseLearners courseId={fullCourse.id} courseName={fullCourse.title} />
              )}
            </Tab>
          )}
        </Tabs>

        {!isOwner && activeTab === 'Coursemates' && (
          <div className="text-center py-4">
            <i className="fa-solid fa-users fa-3x mb-3 text-muted" />
            <p className="text-muted">
              In order to see others taking this course, please add the course first.
            </p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="ml-course-modal__footer">
        <FancyButton variant="tertiary" onClick={onHide}>
          Close
        </FancyButton>

        {isOwner ? (
          <div className="course-modal-launch-controls">
            <select
              className="course-modal-launch-mode"
              value={launchMode}
              onChange={(e) => setLaunchMode(e.target.value)}
              aria-label="Mode"
            >
              <option value="classroom">Text</option>
              <option value="animation">Visual</option>
              <option value="scene">Scene</option>
              <option value="exam">Exam</option>
            </select>
            <Button
              variant="primary"
              onClick={() => {
                if (launchMode === 'animation') {
                  if (typeof onStartAnimated === 'function') {
                    onStartAnimated(fullCourse, selectedLessonIndex);
                  }
                } else if (launchMode === 'scene') {
                  if (typeof onStartScene === 'function') {
                    onStartScene(fullCourse, selectedLessonIndex);
                  }
                } else if (launchMode === 'exam') {
                  if (typeof onStartExam === 'function') {
                    onStartExam(fullCourse, selectedLessonIndex);
                  }
                } else {
                  onStart(fullCourse, selectedLessonIndex);
                }
                onHide();
              }}
            >
              {fullCourse.progress === 0 ? 'Start' : 'Continue'}
            </Button>
          </div>
        ) : (
          <Button
            variant="primary"
            onClick={() => {
              if (!isAuthenticated) {
                // Redirect to login if not authenticated
                onHide();
                navigate('/login', { state: { from: `/courses/${fullCourse.id}` } });
              } else {
                onFork(fullCourse);
                onHide();
              }
            }}
          >
            {isAuthenticated ? 'Add Course' : 'Sign In to Add Course'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CourseDetailsModal;
